<script>
import { createPlayground } from 'livecodes'
import { langConfigs, activeEditorMap } from '@/config/languages'
import VueSfcEditor from './VueSfcEditor.vue'

const VUE_LANGS = ['vue2', 'vue3']

export default {
  name: 'CodeEditor',

  components: { VueSfcEditor },

  props: {
    lang:              { type: String,  default: 'javascript' },
    preview:           { type: Boolean, default: false },
    live:              { type: Boolean, default: false },
    value:             { type: String,  default: null },
    withExportDefault: { type: Boolean, default: true },
  },

  data() {
    return {}
  },

  computed: {
    isVueLang() {
      return VUE_LANGS.includes(this.lang)
    },
  },

  created() {
    this._pg              = null
    this._content         = {}
    this._msgHandler      = null
    this._settingFromProp = false
  },

  watch: {
    async value(newVal) {
      if (this.isVueLang || newVal == null) return
      if (newVal === this._content[this.lang]) return
      this._settingFromProp = true
      await this.setContent(newVal)
      this._settingFromProp = false
    },

    async lang(newLang, oldLang) {
      const wasVue = VUE_LANGS.includes(oldLang)
      const nowVue = VUE_LANGS.includes(newLang)

      if (!wasVue && nowVue) {
        // non-vue → vue: destroy old playground, VueSfcEditor mounts itself
        this._pg?.destroy()
        this._pg = null
      } else if (wasVue && !nowVue) {
        // vue → non-vue: DOM switches first, then mount livecodes
        await this.$nextTick()
        await this._mount()
      } else if (!nowVue) {
        // non-vue → non-vue: remount livecodes
        await this._remount()
      }
      // vue → vue: VueSfcEditor handles it via its own lang watcher
    },
    async preview() { if (!this.isVueLang) await this._applyView() },
    async live()    { if (!this.isVueLang) await this._applyAutoupdate() },
  },

  async mounted() {
    if (!this.isVueLang) {
      await this._mount()
    }
    this._initBridge()
  },

  beforeDestroy() {
    this._pg?.destroy()
    if (this._msgHandler) window.removeEventListener('message', this._msgHandler)
  },

  methods: {
    _buildConfig() {
      const base   = langConfigs[this.lang] || langConfigs.javascript
      const active = activeEditorMap[this.lang]
      const stored = this._content[this.lang] ?? this.value ?? null

      const cfg = {
        ...base,
        theme:      'dark',
        view:       this.preview ? 'split' : 'editor',
        autoupdate: this.live,
      }

      if (stored !== null && active) {
        cfg[active] = { ...(cfg[active] || {}), content: stored }
      }

      return cfg
    },

    async _mount() {
      this._pg = await createPlayground(this.$refs.livecodesEl, { config: this._buildConfig() })

      this._pg.watch('code', ({ code }) => {
        if (this._settingFromProp) return
        const active  = activeEditorMap[this.lang]
        const content = code?.[active]?.content
        if (content == null) return

        this._content[this.lang] = content
        this.$emit('input', content)
        this._post('platon:content-change', { lang: this.lang, content })
      })
    },

    async _remount() {
      this._pg?.destroy()
      this._pg = null
      await this._mount()
    },

    async _applyView() {
      // setConfig ignores view changes — remount is the only reliable way
      await this._remount()
    },

    async _applyAutoupdate() {
      if (!this._pg) return
      try {
        await this._pg.setConfig({ autoupdate: this.live })
      } catch {
        await this._remount()
      }
    },

    async run() {
      if (!this.isVueLang) await this._remount()
    },

    async getContent() {
      if (this.isVueLang) {
        return this.$refs.sfcEditor?.getContent?.() ?? ''
      }
      if (!this._pg) return ''
      try {
        const code   = await this._pg.getCode()
        const active = activeEditorMap[this.lang]
        return code?.[active]?.content ?? ''
      } catch {
        return this._content[this.lang] ?? ''
      }
    },

    async setContent(content) {
      this._content[this.lang] = content
      await this._remount()
    },

    _post(type, payload = {}) {
      if (window.self === window.top) return
      window.parent.postMessage({ source: 'platon-editor', type, ...payload }, '*')
    },

    _initBridge() {
      this._msgHandler = async ({ data }) => {
        if (!data || typeof data !== 'object' || data.target !== 'platon-editor') return

        if (data.type === 'platon:set-lang') {
          this.$emit('update:lang', data.lang)
        }

        if (data.type === 'platon:set-content') {
          await this.setContent(data.content)
        }

        if (data.type === 'platon:get-content') {
          const content = await this.getContent()
          this._post('platon:content', { lang: this.lang, content })
        }
      }

      window.addEventListener('message', this._msgHandler)
      this._post('platon:ready', { lang: this.lang })
    },
  },

  render(h) {
    if (this.isVueLang) {
      return h('div', { style: 'width:100%;height:100%;' }, [
        h(VueSfcEditor, {
          ref: 'sfcEditor',
          props: {
            lang:              this.lang,
            showPreview:       this.preview,
            live:              this.live,
            value:             this.value,
            withExportDefault: this.withExportDefault,
          },
          on: {
            input: (content) => this.$emit('input', content),
          },
        }),
      ])
    }

    const children = []

    if (this.preview) {
      children.push(h('div', { class: 'ce-toolbar' }, [
        h('button', {
          class: ['ce-run-btn', this.live ? 'ce-run-btn--disabled' : ''],
          attrs: { disabled: this.live },
          on: { click: () => this._remount() },
        }, ['▶ Run']),
      ]))
    }

    children.push(h('div', { ref: 'livecodesEl', style: 'flex:1;min-height:0;' }))

    return h('div', { style: 'width:100%;height:100%;display:flex;flex-direction:column;' }, children)
  },
}
</script>

<style>
.ce-toolbar {
  display: flex;
  align-items: center;
  padding: 0 8px;
  height: 36px;
  background: #252526;
  border-bottom: 1px solid #3c3c3c;
  flex-shrink: 0;
}

.ce-run-btn {
  padding: 4px 12px;
  font-size: 12px;
  background: #0e639c;
  color: #fff;
  border: none;
  border-radius: 3px;
  cursor: pointer;
}

.ce-run-btn:hover:not(:disabled) {
  background: #1177bb;
}

.ce-run-btn--disabled {
  background: #3c3c3c;
  color: #6b6b6b;
  cursor: not-allowed;
}
</style>
